<template>
  <div
    class="app"
  >
    <div class="app-masthead">
      <a
        href="/"
        class="app-masthead-logo"
        aria-label="PbcBay home"
      />
    </div>
    <alert
      :alerts="alerts"
      @dismiss="removeAlert"
    />
    <navigation
      v-if="isLoggedIn"
      v-model:is-toggled="isToggled"
      :is-logged-in="isLoggedIn"
      :user="user"
      @logout="logout"
    />
    <router-view v-slot="{Component}">
      <transition
        name="fade"
        mode="out-in"
      >
        <component
          :is="Component"
          class="app-view"
          :user="user"
        />
      </transition>
    </router-view>
  </div>
</template>

<script>
import axios from 'axios';
import Navigation from './partials/Navigation.vue';
import Alert from './partials/Alert.vue';
import Message from './partials/Message';
import WebSocket from './partials/WebSocket';

export default {
  name: 'App',
  components: {
    Navigation,
    Alert,
  },
  data() {
    return {
      isLoggedIn: window.Laravel.loggedIn,
      user: window.Laravel.user ? window.Laravel.user : {},
      alerts: [],
      alertDismissTimeout: 10000,
      lastAlertId: 0,
      isToggled: false,
      websocket: new WebSocket(),
    };
  },
  created() {
    if (window.message) {
      this.setAlert(window.message.detail, window.message.type);
    }
  },
  mounted() {
    window.addEventListener('successMessage', (event) => {
      this.setAlert(event.detail, 'success');
    });

    window.addEventListener('errorMessage', (event) => {
      this.setAlert(event.detail, 'error');
    });
  },
  beforeUnmount() {
    window.removeEventListener('successMessage', (event) => {
      this.setAlert(event.detail, 'success');
    });

    window.removeEventListener('errorMessage', (event) => {
      this.setAlert(event.detail, 'error');
    });
  },
  methods: {
    /**
     * Alerts carry their own id rather than being addressed by their place in
     * the list, so that dismissing one does not retire a different one's
     * timer or tear down its element.
     */
    setAlert(message, alertType) {
      const vm = this;
      vm.lastAlertId += 1;
      const id = vm.lastAlertId;

      vm.alerts.push({
        id,
        message,
        alertType,
        timeout: setTimeout(() => {
          vm.removeAlert(id);
        }, vm.alertDismissTimeout),
      });
    },
    removeAlert(id) {
      const index = this.alerts.findIndex((alert) => alert.id === id);

      if (index === -1) {
        return;
      }

      clearTimeout(this.alerts[index].timeout);
      this.alerts.splice(index, 1);
    },
    logout() {
      this.successMessage = null;
      this.errorMessage = null;
      axios.post('/logout')
        .then(() => {
          window.location = '/';
        })
        .catch((failure) => {
          // 419 means the token or session has already expired, so the user is
          // logged out regardless; send them home like a success.
          if (failure.response && failure.response.status === 419) {
            window.location = '/';

            return;
          }
          new Message('error', 'There was an issue logging out. Please clear cookies.').dispatch();
        });
    },
  },
};
</script>
<style scoped>
@reference "tailwindcss";

.app {
  @apply min-h-screen;
}

/**
 * The masthead is absolutely positioned, so .app-view clears it with its
 * own top padding rather than leaving headings flush against it.
 */
.app-masthead {
  @apply absolute inset-x-0 top-0 h-16 w-full border-solid border-b flex items-center justify-end pr-4;
  background-color: var(--surface);
  border-color: var(--border);
}

/**
 * The negative lockup: transparent ground, white "PBC"/"BaY" and the blue
 * bag. It is artwork on nothing rather than artwork on stock, so unlike
 * the printed mark it replaced, it neither fills the bar's height nor sits
 * flush against the edge — with no paper to end on, both read as the logo
 * being cropped. It is inset instead, and the gutter matches .app-view's.
 */
.app-masthead-logo {
  @apply block;
  background: transparent url('/img/logos/bob_pbcbay_neg-trimmed.png') center center no-repeat;
  background-size: contain;
  /* Width tracks the height at the artwork's 1670x469 content ratio, so the
     box stays the shape of the lockup and never letterboxes. */
  height: 2.25rem;
  width: 8.01rem;
}

.app-view {
  @apply px-4 pb-8;
  padding-top: 5rem;
}

@media (width >= theme(--breakpoint-md)) {
  .app {
    @apply flex items-stretch;
  }

  .app-masthead {
    @apply h-20 pr-6;
  }

  .app-masthead-logo {
    height: 2.75rem;
    width: 9.79rem;
  }

  /**
   * flex-grow, but never self-center: inside an items-stretch row that
   * centred every page's content against the viewport height, so a short
   * page floated in the middle of the screen.
   */
  .app-view {
    @apply flex-col grow px-6;
    padding-top: 6rem;
  }
}

@media (width >= theme(--breakpoint-lg)) {
  .app-masthead {
    @apply h-24;
  }

  .app-masthead-logo {
    width: 12.75rem;
  }

  .app-view {
    padding-top: 7rem;
  }
}
</style>
